import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Table, Modal, Alert, Badge, Tabs, Tab, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const PuntosReunion = () => {
  const { user, loading: authLoading } = useAuth();
  const [puntosReunion, setPuntosReunion] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [filtroMes, setFiltroMes] = useState(new Date().getMonth() + 1);
  const [filtroAño, setFiltroAño] = useState(new Date().getFullYear());
  const [message, setMessage] = useState(null);
  const [loadingAsistentes, setLoadingAsistentes] = useState(false);
  const [socios, setSocios] = useState([]);
  const [loadingSocios, setLoadingSocios] = useState(false);
  const [socioSeleccionado, setSocioSeleccionado] = useState(null);
  const [familiaSeleccionada, setFamiliaSeleccionada] = useState(null);

  // Verificar permisos de acceso
  const canAccess = user && ['secretaria', 'admin', 'super-admin'].includes(user.role);

  // Función para obtener la fecha actual de Ecuador (UTC-5)
  const obtenerFechaEcuador = () => {
    try {
      // Obtener la hora actual y ajustar a UTC-5 (Ecuador)
      const ahora = new Date();
      const offset = -5 * 60 * 60 * 1000; // UTC-5 en milisegundos
      const utcTime = ahora.getTime() + ahora.getTimezoneOffset() * 60 * 1000;
      const ecuadorTime = new Date(utcTime + offset);
      
      // Formatear como YYYY-MM-DD
      const año = ecuadorTime.getFullYear();
      const mes = String(ecuadorTime.getMonth() + 1).padStart(2, '0');
      const día = String(ecuadorTime.getDate()).padStart(2, '0');
      
      return `${año}-${mes}-${día}`;
    } catch (error) {
      console.error('Error al obtener fecha de Ecuador:', error);
      // Fallback: usar la fecha local
      const ahora = new Date();
      const año = ahora.getFullYear();
      const mes = String(ahora.getMonth() + 1).padStart(2, '0');
      const día = String(ahora.getDate()).padStart(2, '0');
      return `${año}-${mes}-${día}`;
    }
  };

  // Estados para el formulario
  const [formData, setFormData] = useState({
    fechaReunion: obtenerFechaEcuador(),
    tipoReunion: 'reunion_ordinaria',
    lugarReunion: '',
    horaInicio: '09:00',
    horaFin: '10:00',
    resumen: '',
    asistentes: [],
    puntos: [{ tema: '', descripcion: '', responsable: '', estado: 'pendiente' }],
    familiaId: null, // ID de la familia si pertenece a una
    fechaReunionFamilia: null, // Fecha específica de la familia
    horaInicioFamilia: null, // Hora específica de la familia
    horaFinFamilia: null // Hora fin específica de la familia
  });

  // Cargar datos
  useEffect(() => {
    cargarPuntosReunion();
  }, [filtroMes, filtroAño]);

  // Función para cargar asistentes desde la API de asistencia
  const cargarAsistentesAutomaticos = async () => {
    try {
      setLoadingAsistentes(true);
      
      // Obtener la fecha actual de Ecuador para determinar mes/año
      const fechaEcuador = new Date(obtenerFechaEcuador());
      const mesActual = fechaEcuador.getMonth() + 1;
      const añoActual = fechaEcuador.getFullYear();

      // Cargar asistencia del mes actual
      const response = await axios.get('/api/asistencia', {
        params: {
          mes: mesActual,
          año: añoActual
        }
      });

      // Filtrar solo los que asistieron
      const asistentesPresentes = response.data.data
        .filter(a => a.asistio === true)
        .map(a => ({
          nombre: a.socio.name,
          rol: a.socio.socioInfo?.numeroSocio || 'Socio',
          presente: true,
          socioId: a.socio._id
        }));

      // Actualizar el formulario con los asistentes cargados
      setFormData(prev => ({
        ...prev,
        fechaReunion: obtenerFechaEcuador(),
        asistentes: asistentesPresentes.length > 0 ? asistentesPresentes : [{ nombre: '', rol: '', presente: true }]
      }));

      setLoadingAsistentes(false);
    } catch (error) {
      console.error('Error al cargar asistentes:', error);
      setMessage({ type: 'warning', text: 'No se pudieron cargar los asistentes automáticamente' });
      setLoadingAsistentes(false);
    }
  };

  // Función para cargar lista de socios
  const cargarSocios = async () => {
    try {
      setLoadingSocios(true);
      const response = await axios.get('/api/users?role=socio&limit=1000');
      setSocios(response.data.users || []);
    } catch (error) {
      console.error('Error al cargar socios:', error);
      setMessage({ type: 'warning', text: 'No se pudieron cargar los socios' });
    } finally {
      setLoadingSocios(false);
    }
  };

  const cargarPuntosReunion = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/puntos-reunion', {
        params: {
          mes: filtroMes,
          año: filtroAño
        }
      });
      setPuntosReunion(response.data.data);
    } catch (error) {
      setMessage({ type: 'danger', text: 'Error al cargar los datos: ' + error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Manejar selección de socio anfitrión
  const handleSocioChange = async (e) => {
    const socioId = e.target.value;
    const socio = socios.find(s => s._id === socioId);
    
    if (socio) {
      setSocioSeleccionado(socio);
      // Llenar automáticamente el lugar con la dirección del socio
      setFormData(prev => ({
        ...prev,
        lugarReunion: socio.address || 'Domicilio de ' + socio.name,
        familiaId: null,
        fechaReunionFamilia: null,
        horaInicioFamilia: null,
        horaFinFamilia: null
      }));

      // Intentar cargar la familia del socio
      try {
        const response = await axios.get(`/api/familias/user/${socioId}`);
        if (response.data.success && response.data.familia) {
          const familia = response.data.familia;
          setFamiliaSeleccionada(familia);

          // Cargar automáticamente todos los miembros de la familia como asistentes
          const miembrosAsistentes = familia.miembros.map(miembro => ({
            nombre: miembro.nombre,
            rol: miembro.rol,
            presente: true,
            socioId: miembro.userId,
            familyMember: true
          }));

          // Usar fecha/hora de la familia si están configuradas
          const fechaFamilia = familia.reunion?.fecha ? new Date(familia.reunion.fecha).toISOString().split('T')[0] : obtenerFechaEcuador();
          const horaInicioFamilia = familia.reunion?.horaInicio || '09:00';
          const horaFinFamilia = familia.reunion?.horaFin || '10:00';

          setFormData(prev => ({
            ...prev,
            lugarReunion: familia.reunion?.lugar || socio.address || 'Domicilio de ' + socio.name,
            asistentes: miembrosAsistentes,
            familiaId: familia._id,
            fechaReunionFamilia: familia.reunion?.fecha,
            horaInicioFamilia: horaInicioFamilia,
            horaFinFamilia: horaFinFamilia,
            fechaReunion: fechaFamilia,
            horaInicio: horaInicioFamilia,
            horaFin: horaFinFamilia
          }));
        } else {
          // No pertenece a familia, solo mostrar el socio
          setFamiliaSeleccionada(null);
          setFormData(prev => ({
            ...prev,
            asistentes: [{
              nombre: socio.name,
              rol: socio.socioInfo?.numeroSocio || 'Socio',
              presente: true,
              socioId: socio._id
            }]
          }));
        }
      } catch (error) {
        // No está en una familia, que es OK
        setFamiliaSeleccionada(null);
        setFormData(prev => ({
          ...prev,
          asistentes: [{
            nombre: socio.name,
            rol: socio.socioInfo?.numeroSocio || 'Socio',
            presente: true,
            socioId: socio._id
          }]
        }));
      }
    } else {
      setSocioSeleccionado(null);
      setFamiliaSeleccionada(null);
      setFormData(prev => ({
        ...prev,
        lugarReunion: '',
        asistentes: [],
        familiaId: null,
        fechaReunionFamilia: null,
        horaInicioFamilia: null,
        horaFinFamilia: null
      }));
    }
  };

  // Manejar selección de mes
  const handleMesChange = (e) => {
    const mes = parseInt(e.target.value);
    setFormData(prev => ({
      ...prev,
      // No cambiamos fecha, solo lo usamos para contexto
    }));
    // El mes se usa implícitamente en cargarAsistentesAutomaticos
  };

  const handleAsistenteChange = (index, field, value) => {
    const newAsistentes = [...formData.asistentes];
    newAsistentes[index][field] = value;
    setFormData(prev => ({ ...prev, asistentes: newAsistentes }));
  };

  const handlePuntoChange = (index, field, value) => {
    const newPuntos = [...formData.puntos];
    newPuntos[index][field] = value;
    setFormData(prev => ({ ...prev, puntos: newPuntos }));
  };

  const agregarAsistente = () => {
    setFormData(prev => ({
      ...prev,
      asistentes: [...prev.asistentes, { nombre: '', rol: '', presente: true }]
    }));
  };

  const agregarPunto = () => {
    setFormData(prev => ({
      ...prev,
      puntos: [...prev.puntos, { tema: '', descripcion: '', responsable: '', estado: 'pendiente' }]
    }));
  };

  const eliminarAsistente = (index) => {
    setFormData(prev => ({
      ...prev,
      asistentes: prev.asistentes.filter((_, i) => i !== index)
    }));
  };

  const eliminarPunto = (index) => {
    setFormData(prev => ({
      ...prev,
      puntos: prev.puntos.filter((_, i) => i !== index)
    }));
  };

  const resetFormulario = () => {
    setFormData({
      fechaReunion: obtenerFechaEcuador(),
      tipoReunion: 'reunion_ordinaria',
      lugarReunion: '',
      horaInicio: '09:00',
      horaFin: '10:00',
      resumen: '',
      asistentes: [],
      puntos: [{ tema: '', descripcion: '', responsable: '', estado: 'pendiente' }],
      familiaId: null,
      fechaReunionFamilia: null,
      horaInicioFamilia: null,
      horaFinFamilia: null
    });
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      if (editingId) {
        await axios.put(`/api/puntos-reunion/${editingId}`, formData);
        setMessage({ type: 'success', text: 'Acta actualizada exitosamente' });
      } else {
        await axios.post('/api/puntos-reunion', formData);
        setMessage({ type: 'success', text: 'Acta de reunión creada exitosamente' });
      }
      // ✅ ESPERAR A QUE TERMINE DE RECARGAR ANTES DE CERRAR
      await cargarPuntosReunion();
      setShowModal(false);
      resetFormulario();
    } catch (error) {
      setMessage({ type: 'danger', text: 'Error: ' + error.response?.data?.message || error.message });
    } finally {
      setLoading(false);
    }
  };

  const abrirEdicion = (id) => {
    const reunion = puntosReunion.find(r => r._id === id);
    if (reunion) {
      setFormData({
        fechaReunion: reunion.fechaReunion.split('T')[0],
        tipoReunion: reunion.tipoReunion,
        lugarReunion: reunion.lugarReunion,
        horaInicio: reunion.horaInicio,
        horaFin: reunion.horaFin,
        resumen: reunion.resumen,
        asistentes: reunion.asistentes,
        puntos: reunion.puntos
      });
      setEditingId(id);
      setShowModal(true);
    }
  };

  // Función para abrir nuevo modal y cargar asistentes automáticamente
  const abrirNuevaReunion = async () => {
    resetFormulario();
    setSocioSeleccionado(null);
    setShowModal(true);
    // Cargar socios y asistentes automáticamente después de abrir el modal
    await Promise.all([cargarSocios(), cargarAsistentesAutomaticos()]);
  };

  const enviarRevision = async (id) => {
    try {
      await axios.put(`/api/puntos-reunion/${id}/enviar-revision`);
      setMessage({ type: 'success', text: 'Acta enviada a revisión' });
      cargarPuntosReunion();
    } catch (error) {
      setMessage({ type: 'danger', text: 'Error: ' + error.response?.data?.message });
    }
  };

  const eliminar = async (id) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar esta acta?')) {
      try {
        await axios.delete(`/api/puntos-reunion/${id}`);
        setMessage({ type: 'success', text: 'Acta eliminada exitosamente' });
        cargarPuntosReunion();
      } catch (error) {
        setMessage({ type: 'danger', text: 'Error: ' + error.response?.data?.message });
      }
    }
  };

  const getBadgeColor = (estado) => {
    switch (estado) {
      case 'aprobado': return 'success';
      case 'pendiente_revision': return 'warning';
      case 'borrador': return 'secondary';
      case 'rechazado': return 'danger';
      default: return 'info';
    }
  };

  const getNombreMes = (mes) => {
    const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    return meses[mes - 1];
  };

  // Mostrar spinner mientras carga
  if (authLoading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
        <Row>
          <Col className="text-center">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3 text-muted">Cargando...</p>
          </Col>
        </Row>
      </Container>
    );
  }

  // Verificar permisos de acceso
  if (!canAccess) {
    return (
      <Container className="mt-5">
        <Row className="justify-content-center">
          <Col md={6} className="text-center">
            <div className="card-custom p-4">
              <i className="fas fa-exclamation-triangle text-warning" style={{ fontSize: '3rem' }}></i>
              <h3 className="mt-3 text-danger">Acceso Denegado</h3>
              <p className="text-muted">
                No tienes permisos para acceder a esta sección. 
                Se requiere ser Secretaria o Administrador.
              </p>
              <button 
                className="btn btn-primary-custom"
                onClick={() => window.history.back()}
              >
                <i className="fas fa-arrow-left me-2"></i>
                Volver
              </button>
            </div>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4">
      <Row className="mb-4">
        <Col>
          <h2><i className="fas fa-clipboard-list me-2"></i>Puntos de Reunión</h2>
        </Col>
      </Row>

      {message && (
        <Alert variant={message.type} onClose={() => setMessage(null)} dismissible>
          {message.text}
        </Alert>
      )}

      {/* Filtros y botón nuevo */}
      <Row className="mb-4">
        <Col md={3}>
          <Form.Group>
            <Form.Label>Mes</Form.Label>
            <Form.Select value={filtroMes} onChange={(e) => setFiltroMes(parseInt(e.target.value))}>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(m => (
                <option key={m} value={m}>{getNombreMes(m)}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Año</Form.Label>
            <Form.Select value={filtroAño} onChange={(e) => setFiltroAño(parseInt(e.target.value))}>
              {[2024, 2025, 2026, 2027, 2028].map(a => (
                <option key={a} value={a}>{a}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={6} className="d-flex align-items-end">
          <Button variant="success" onClick={abrirNuevaReunion} disabled={loadingAsistentes}>
            {loadingAsistentes ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Cargando asistentes...
              </>
            ) : (
              <>
                <i className="fas fa-plus me-2"></i>Nueva Acta de Reunión
              </>
            )}
          </Button>
        </Col>
      </Row>

      {/* Lista de actas */}
      <Row>
        <Col>
          {loading ? (
            <div className="text-center"><p>Cargando...</p></div>
          ) : puntosReunion.length === 0 ? (
            <Alert variant="info">No hay actas de reunión para este mes</Alert>
          ) : (
            <div className="table-responsive">
              <Table striped bordered hover size="sm">
                <thead className="table-dark">
                  <tr>
                    <th>Fecha</th>
                    <th>Tipo</th>
                    <th>Lugar</th>
                    <th>Puntos</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {puntosReunion.map(reunion => (
                    <tr key={reunion._id}>
                      <td>{new Date(reunion.fechaReunion).toLocaleDateString()}</td>
                      <td>{reunion.tipoReunion.replace(/_/g, ' ').toUpperCase()}</td>
                      <td>{reunion.lugarReunion}</td>
                      <td className="text-center">
                        <Badge bg="info">{reunion.puntos.length}</Badge>
                      </td>
                      <td>
                        <Badge bg={getBadgeColor(reunion.estado_aprobacion)}>
                          {reunion.estado_aprobacion}
                        </Badge>
                      </td>
                      <td>
                        <Button
                          variant="primary"
                          size="sm"
                          className="me-2"
                          onClick={() => abrirEdicion(reunion._id)}
                        >
                          <i className="fas fa-eye"></i>
                        </Button>
                        {reunion.estado_aprobacion === 'borrador' && (
                          <>
                            <Button
                              variant="warning"
                              size="sm"
                              className="me-2"
                              onClick={() => enviarRevision(reunion._id)}
                            >
                              <i className="fas fa-check"></i>
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => eliminar(reunion._id)}
                            >
                              <i className="fas fa-trash"></i>
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Col>
      </Row>

      {/* Modal de formulario */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" scrollable>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingId ? 'Editar Acta de Reunión' : 'Nueva Acta de Reunión'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Tabs defaultActiveKey="info" className="mb-3">
              {/* Información General */}
              <Tab eventKey="info" title="Información General">
                <Row className="mt-3">
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Fecha de Reunión</Form.Label>
                      <Form.Control
                        type="date"
                        name="fechaReunion"
                        value={formData.fechaReunion}
                        onChange={handleInputChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Socio Anfitrión
                        {familiaSeleccionada && (
                          <Badge bg="success" className="ms-2">
                            <i className="fas fa-users me-1"></i>Familia asignada
                          </Badge>
                        )}
                      </Form.Label>
                      <div className="d-flex gap-2">
                        <Form.Select
                          value={socioSeleccionado?._id || ''}
                          onChange={handleSocioChange}
                          disabled={loadingSocios || !!familiaSeleccionada}
                        >
                          <option value="">-- Selecciona un socio --</option>
                          {socios.map(socio => (
                            <option key={socio._id} value={socio._id}>
                              {socio.name}
                            </option>
                        ))}
                      </Form.Select>
                      {familiaSeleccionada && (
                        <Button 
                          variant="outline-secondary" 
                          size="sm"
                          onClick={() => {
                            setFamiliaSeleccionada(null);
                            setSocioSeleccionado(null);
                            setFormData(prev => ({
                              ...prev,
                              asistentes: [],
                              lugarReunion: '',
                              familiaId: null,
                              fechaReunionFamilia: null,
                              horaInicioFamilia: null,
                              horaFinFamilia: null,
                              horaInicio: '09:00',
                              horaFin: '10:00'
                            }));
                          }}
                        >
                          <i className="fas fa-times"></i>
                        </Button>
                      )}
                      </div>
                      {loadingSocios && <small className="text-muted">Cargando socios...</small>}
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Lugar de Reunión</Form.Label>
                      <Form.Control
                        type="text"
                        name="lugarReunion"
                        placeholder="Se llena automáticamente según el socio seleccionado"
                        value={formData.lugarReunion}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Hora Inicio</Form.Label>
                      <Form.Control
                        type="time"
                        name="horaInicio"
                        value={formData.horaInicio}
                        onChange={handleInputChange}
                      />
                      {familiaSeleccionada && (
                        <Form.Text className="text-muted">
                          Configurada en familia: {formData.horaInicioFamilia}
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Hora Fin</Form.Label>
                      <Form.Control
                        type="time"
                        name="horaFin"
                        value={formData.horaFin}
                        onChange={handleInputChange}
                      />
                      {familiaSeleccionada && (
                        <Form.Text className="text-muted">
                          Configurada en familia: {formData.horaFinFamilia}
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                {familiaSeleccionada && (
                  <Row className="mb-3">
                    <Col>
                      <Alert variant="success" className="border-2">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <i className="fas fa-check-circle me-2 text-success"></i>
                            <strong>Familia Asignada: {familiaSeleccionada.nombre}</strong>
                            <br/>
                            <small className="text-muted">
                              {familiaSeleccionada.miembros.length} miembro{familiaSeleccionada.miembros.length !== 1 ? 's' : ''} se registrarán automáticamente
                            </small>
                            <div className="mt-2 text-muted" style={{ fontSize: '0.9rem' }}>
                              <strong>Miembros:</strong> {familiaSeleccionada.miembros.map(m => m.nombre).join(', ')}
                            </div>
                          </div>
                        </div>
                      </Alert>
                    </Col>
                  </Row>
                )}

                <Form.Group className="mb-3">
                  <Form.Label>Resumen</Form.Label>
                  <Form.Control
                    as="textarea"
                    name="resumen"
                    rows={3}
                    placeholder="Resumen general de la reunión"
                    value={formData.resumen}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Tab>

              {/* Asistentes */}
              <Tab eventKey="asistentes" title="Asistentes">
                <div className="mt-3">
                  {!editingId && (
                    <Alert variant="info">
                      <i className="fas fa-info-circle me-2"></i>
                      Los asistentes se han cargado automáticamente de los registros de asistencia del mes actual.
                      Puedes editar, agregar o eliminar asistentes si es necesario.
                    </Alert>
                  )}
                  {formData.asistentes.length === 0 ? (
                    <Alert variant="warning">
                      No hay asistentes registrados. Agrega al menos uno.
                    </Alert>
                  ) : (
                    formData.asistentes.map((asistente, index) => (
                      <Row key={index} className="mb-3">
                        <Col md={4}>
                          <Form.Control
                            type="text"
                            placeholder="Nombre"
                            value={asistente.nombre}
                            onChange={(e) => handleAsistenteChange(index, 'nombre', e.target.value)}
                          />
                        </Col>
                        <Col md={4}>
                          <Form.Control
                            type="text"
                            placeholder="Rol/Cargo"
                            value={asistente.rol}
                            onChange={(e) => handleAsistenteChange(index, 'rol', e.target.value)}
                          />
                        </Col>
                        <Col md={2}>
                          <Form.Check
                            type="checkbox"
                            label="Presente"
                            checked={asistente.presente}
                            onChange={(e) => handleAsistenteChange(index, 'presente', e.target.checked)}
                          />
                        </Col>
                        <Col md={2}>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => eliminarAsistente(index)}
                          >
                            <i className="fas fa-times"></i>
                          </Button>
                        </Col>
                      </Row>
                    ))
                  )}
                  <Button variant="secondary" size="sm" onClick={agregarAsistente}>
                    <i className="fas fa-plus me-2"></i>Agregar Asistente
                  </Button>
                </div>
              </Tab>

              {/* Puntos a Tratar */}
              <Tab eventKey="puntos" title="Puntos a Tratar">
                <div className="mt-3">
                  {formData.puntos.map((punto, index) => (
                    <Card key={index} className="mb-3">
                      <Card.Body>
                        <Row>
                          <Col md={12}>
                            <Form.Group className="mb-2">
                              <Form.Label><strong>Punto {index + 1}: Tema</strong></Form.Label>
                              <Form.Control
                                type="text"
                                placeholder="Tema del punto"
                                value={punto.tema}
                                onChange={(e) => handlePuntoChange(index, 'tema', e.target.value)}
                                required
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-2">
                              <Form.Label>Descripción</Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={2}
                                placeholder="Detalles"
                                value={punto.descripcion}
                                onChange={(e) => handlePuntoChange(index, 'descripcion', e.target.value)}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-2">
                              <Form.Label>Responsable</Form.Label>
                              <Form.Control
                                type="text"
                                placeholder="Persona responsable"
                                value={punto.responsable}
                                onChange={(e) => handlePuntoChange(index, 'responsable', e.target.value)}
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-2">
                              <Form.Label>Estado</Form.Label>
                              <Form.Select
                                value={punto.estado}
                                onChange={(e) => handlePuntoChange(index, 'estado', e.target.value)}
                              >
                                <option value="pendiente">Pendiente</option>
                                <option value="en_progreso">En Progreso</option>
                                <option value="completado">Completado</option>
                                <option value="aplazado">Aplazado</option>
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col md={6} className="d-flex align-items-end">
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => eliminarPunto(index)}
                              className="w-100"
                            >
                              <i className="fas fa-trash me-2"></i>Eliminar Punto
                            </Button>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  ))}
                  <Button variant="secondary" size="sm" onClick={agregarPunto}>
                    <i className="fas fa-plus me-2"></i>Agregar Punto
                  </Button>
                </div>
              </Tab>
            </Tabs>

            <div className="mt-4 d-flex gap-2">
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? 'Guardando...' : editingId ? 'Actualizar' : 'Crear'}
              </Button>
              <Button variant="secondary" onClick={() => setShowModal(false)}>
                Cancelar
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default PuntosReunion;
